import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { ApiError } from '../lib/api';
import type { Tone } from '../lib/labels';

/* ---------- buttons ---------- */
type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'green' | 'danger' | 'ghost' | 'default';
  size?: 'sm' | 'md';
  busy?: boolean;
  icon?: ReactNode;
};
export function Button({ variant = 'default', size = 'md', busy, icon, className = '', children, disabled, ...rest }: BtnProps) {
  const cls = ['btn', variant !== 'default' ? variant : '', size === 'sm' ? 'sm' : '', !children ? 'icon' : '', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : icon}
      {children}
    </button>
  );
}

/* ---------- form fields ---------- */
export function Field({
  label,
  hint,
  error,
  children,
  className = '',
  htmlFor,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  // Tie the label to its control even when the caller did not pass htmlFor, so screen
  // readers announce it and tests can find the control by its label.
  const autoId = useId();
  let control = children;
  let forId = htmlFor;
  if (!forId && label && isValidElement<{ id?: string }>(children)) {
    forId = children.props.id ?? autoId;
    if (!children.props.id) control = cloneElement(children, { id: autoId });
  }
  return (
    <div className={`field ${error ? 'invalid' : ''} ${className}`}>
      {label ? <label htmlFor={forId}>{label}</label> : null}
      {control}
      {error ? <span className="error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export const Input = (props: InputHTMLAttributes<HTMLInputElement>) => <input className="input" {...props} />;
export const Textarea = (props: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className="textarea" {...props} />;
export function Select({ options, placeholder, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string | number; label: string }[]; placeholder?: string }) {
  return (
    <select className="select" {...props}>
      {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * A small "i" button that shows a field's help text on demand, so long help does not crowd the form. Place it in an
 * element with the info-host class (position: relative): the note opens below that element and spans its width, so it
 * never widens the page on a phone. `describedBy` names the field the help is about, for screen readers.
 */
export function InfoButton({ children, describedBy, label = 'More information' }: { children: ReactNode; describedBy?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // Moving on to another control, by click, tab or a script, closes it too.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('focusin', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('focusin', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span className="info" ref={ref}>
      <button type="button" className="info-btn" aria-label={label} aria-describedby={describedBy} aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}>
        <Info size={13} aria-hidden />
      </button>
      {open ? (
        <span className="info-pop" id={id} role="note">
          {children}
        </span>
      ) : null}
    </span>
  );
}

/* ---------- display ---------- */
export function Badge({ tone = '', children, dot }: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  return (
    <span className={`badge ${tone}`}>
      {dot ? <span className="dot" /> : null}
      {children}
    </span>
  );
}

/** Badge for an enum value using a [label, tone] map from lib/labels. */
export function StatusBadge({ map, value }: { map: Record<string, [string, Tone]>; value: string | null | undefined }) {
  if (!value) return null;
  const [text, t] = map[value] ?? [value.replace(/_/g, ' '), ''];
  return <Badge tone={t}>{text}</Badge>;
}

export function Card({ title, actions, children, flush, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {title || actions ? (
        <div className="card-head">
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      <div className={`card-body ${flush ? 'flush' : ''}`}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, crumbs, actions }: { title: ReactNode; subtitle?: ReactNode; crumbs?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="titles">
        {crumbs ? <div className="crumbs">{crumbs}</div> : null}
        <h1>{title}</h1>
        {subtitle ? <p className="muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </header>
  );
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      {children ? <p className="small">{children}</p> : null}
      {action}
    </div>
  );
}

export const Loading = ({ label = 'Loading' }: { label?: string }) => (
  <div className="loading" role="status">
    <span className="spinner" />
    <span className="sr-only">{label}</span>
  </div>
);

export function Banner({ tone = 'info', children, onClose }: { tone?: 'info' | 'warn' | 'error' | 'ok'; children: ReactNode; onClose?: () => void }) {
  const Icon = tone === 'error' || tone === 'warn' ? AlertTriangle : tone === 'ok' ? CheckCircle2 : Info;
  return (
    <div className={`banner ${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <Icon />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose ? (
        <button className="btn ghost sm icon" onClick={onClose} aria-label="Dismiss">
          <X />
        </button>
      ) : null}
    </div>
  );
}

/** Shows an API error (or any error) as a banner. */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  return <Banner tone="error">{msg}</Banner>;
}

export function fieldError(error: unknown, name: string): string | undefined {
  return error instanceof ApiError ? error.fields[name] : undefined;
}

export function Progress({ value, total }: { value: number; total: number }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ---------- modal ---------- */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="stack tight">
            <h2>{title}</h2>
            {subtitle ? <p className="muted small">{subtitle}</p> : null}
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** Asks before running a destructive or irreversible action. */
export function useConfirm() {
  const [state, setState] = useState<{ message: ReactNode; resolve: (v: boolean) => void; confirmLabel: string } | null>(null);
  const confirm = useCallback(
    (message: ReactNode, confirmLabel = 'Confirm') => new Promise<boolean>((resolve) => setState({ message, resolve, confirmLabel })),
    [],
  );
  const dialog = state ? (
    <Modal
      title="Please confirm"
      onClose={() => {
        state.resolve(false);
        setState(null);
      }}
      footer={
        <>
          <Button
            onClick={() => {
              state.resolve(false);
              setState(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              state.resolve(true);
              setState(null);
            }}
          >
            {state.confirmLabel}
          </Button>
        </>
      }
    >
      <div className="small">{state.message}</div>
    </Modal>
  ) : null;
  return { confirm, dialog };
}

/* ---------- toasts ---------- */
type Toast = { id: number; text: string; error?: boolean };
const ToastContext = createContext<(text: string, error?: boolean) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const push = useCallback((text: string, error?: boolean) => {
    const id = nextId.current++;
    const life = error ? 6000 : 3500;
    // The same message again replaces the one still showing, so a repeated action never stacks identical toasts.
    setToasts((t) => [...t.filter((x) => x.text !== text || !!x.error !== !!error), { id, text, error }]);
    // A toast stays for its full time while the page can be read. Long work in the browser right after the action
    // the toast reports (laying out print packs, building a model) holds the page, sometimes in several chunks, and a
    // plain timer would let the toast expire unseen. So only time the page is actually drawing counts: each animation
    // frame adds the time since the last one, and a gap longer than 250 ms (the page was held, or the tab hidden)
    // adds nothing.
    let shown = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const gap = now - last;
      last = now;
      if (gap < 250) shown += gap;
      if (shown >= life) setToasts((t) => t.filter((x) => x.id !== id));
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.error ? 'error' : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** toast('Saved.') or toast(err) for an error. */
export function useToast() {
  const push = useContext(ToastContext);
  return useCallback(
    (msg: string | unknown) => {
      if (typeof msg === 'string') push(msg);
      else push(msg instanceof Error ? msg.message : 'Something went wrong. Please try again.', true);
    },
    [push],
  );
}

/* ---------- tabs ---------- */
/**
 * For a row that scrolls sideways on a phone (tabs, the phase steps): sets data-more to "left",
 * "right" or "both" while part of the row is hidden, so CSS fades that edge (see .scroll-hint),
 * and scrolls the selected item into view when `activeKey` changes.
 */
export function useScrollHint<T extends HTMLElement>(activeKey?: unknown) {
  const ref = useRef<T>(null);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const more = left && right ? 'both' : left ? 'left' : right ? 'right' : '';
    if (more) el.dataset.more = more;
    else delete el.dataset.more;
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [update]);
  // Labels and counts change the row's width without resizing the row itself, so check after every render.
  useLayoutEffect(update);
  useEffect(() => {
    const el = ref.current;
    const active = el?.querySelector<HTMLElement>('[aria-selected="true"], [aria-current="step"]');
    if (!el || !active) return;
    const box = el.getBoundingClientRect();
    const at = active.getBoundingClientRect();
    if (at.left < box.left) el.scrollLeft -= box.left - at.left + 24;
    else if (at.right > box.right) el.scrollLeft += at.right - box.right + 24;
  }, [activeKey]);
  return ref;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode; count?: number }[]; value: T; onChange: (id: T) => void }) {
  const ref = useScrollHint<HTMLDivElement>(value);
  return (
    <div className="tabs scroll-hint" role="tablist" ref={ref}>
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={t.id === value} className={t.id === value ? 'active' : ''} onClick={() => onChange(t.id)}>
          {t.label}
          {t.count !== undefined ? <span className="count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: T; label: ReactNode; icon?: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  /** Names the group for screen readers when a screen has more than one switch. */
  label?: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} className={o.id === value ? 'on' : ''} aria-pressed={o.id === value} onClick={() => onChange(o.id)}>
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}
